// Client-side tenant routing — extracts the tenant slug from the page URL.
//
// Production URL pattern: `{slug}.solvait.ai` (primary as of 2026-05-12).
// `solvait.com` is kept as a recognized apex for marketing redirects /
// pre-2026 bookmarks. Dev / `*.workers.dev` URLs return null slug — the
// tenant in those cases comes from the Clerk JWT's `org_id` claim.
//
// Used by:
//   - `lib/api.ts` — sends `X-Tenant-Slug` on every backend request as
//     defense-in-depth alongside the Clerk JWT.
//   - UI components that want to display the active tenant name.

export interface TenantContext {
  slug: string | null;
  isCustomDomain: boolean;
  hostname: string;
}

// Apex domains we recognize as tenant-bearing. `solvait.ai` is the
// primary; `solvait.com` stays here so legacy URLs keep resolving the
// same slug after the 2026-05 migration.
const APEX_DOMAINS: readonly string[] = ["solvait.ai", "solvait.com"];

// Hostnames where subdomain extraction doesn't apply (dev, preview, etc.).
const NON_TENANT_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
]);

export function readTenantFromUrl(hostname: string = window.location.hostname): TenantContext {
  const host = hostname.toLowerCase().trim();

  // E2E override: Playwright runs against `localhost`, which would
  // otherwise resolve to no-tenant (marketing landing). When the
  // build-time flag is set (M30.5.0), force the tenant slug from
  // the env so the workspace renders.
  if (
    typeof import.meta !== "undefined" &&
    typeof import.meta.env !== "undefined"
  ) {
    const envSlug = (import.meta.env as { VITE_E2E_TENANT_SLUG?: string })
      .VITE_E2E_TENANT_SLUG;
    if (envSlug && typeof envSlug === "string") {
      return {
        slug: envSlug,
        isCustomDomain: false,
        hostname: host,
      };
    }
  }

  if (NON_TENANT_HOSTS.has(host)) {
    return { slug: null, isCustomDomain: false, hostname: host };
  }

  for (const apex of APEX_DOMAINS) {
    // Strict apex match: just `solvait.ai`, no subdomain → no tenant.
    if (host === apex) {
      return { slug: null, isCustomDomain: true, hostname: host };
    }
    if (host.endsWith(`.${apex}`)) {
      const labels = host.slice(0, -`.${apex}`.length).split(".");
      // Only single-label subdomains are tenants. `foo.bar.solvait.ai`
      // is not a tenant; it's a misrouted URL.
      if (labels.length === 1 && labels[0]) {
        return {
          slug: labels[0],
          isCustomDomain: true,
          hostname: host,
        };
      }
      return { slug: null, isCustomDomain: true, hostname: host };
    }
  }

  // `*.workers.dev` (Cloudflare Workers default), `*.up.railway.app`,
  // anything else — no subdomain semantics. Tenant comes from the
  // Clerk JWT instead.
  return { slug: null, isCustomDomain: false, hostname: host };
}

let _cached: TenantContext | null = null;

export function tenantContext(): TenantContext {
  if (_cached === null && typeof window !== "undefined") {
    _cached = readTenantFromUrl();
  }
  return _cached ?? { slug: null, isCustomDomain: false, hostname: "" };
}
